// CSC-297 — thin wrapper around EventSource that adds:
//   * typed event parsing matching the backend's QuotationEventDto
//   * exponential-backoff reconnect on transport errors (1s → 30s)
//   * a connection-state observable so the UI can render a "Live" pill
//
// Auth: EventSource doesn't accept custom headers, so the access token is passed
// as a query param `?access_token=...`. The backend's JwtBearerEvents handler
// (see CSC_WMS_API/Program.cs) extracts that param ONLY for /api/quotations/events
// and feeds it to the JWT pipeline.

import { tokenService } from '@features/authentication/services/tokenService';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import { QuotationStatus } from '../types/quotation.types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
const STREAM_PATH = '/quotations/events';
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

// Mirrors the backend's `QuotationEventType` enum. Numeric so the wire format
// matches the rest of the API (which serializes enums as ints by default).
export enum QuotationEventType {
  Created = 0,
  Updated = 1,
  Deleted = 2,
  Archived = 3,
  Restored = 4,
}

export interface QuotationEvent {
  eventType: QuotationEventType;
  quotationId: number;
  // 86d3mnuxe — entity status (nullable; Draft/AutoDraft/Submitted/Sent). Posting-in-flight / failed ride
  // postingStatus below, NOT the entity status (the posting lifecycle moved to the outbox).
  status: QuotationStatus | null;
  postingStatus: string | null;
  lastError: string | null;
  failureDetailJson: string | null;
  timestamp: string;
}

export type ConnectionState = 'connecting' | 'open' | 'reconnecting';

export interface QuotationEventStreamHandlers {
  onEvent: (evt: QuotationEvent) => void;
  // Fires whenever the underlying EventSource transitions states. The hook uses
  // this to surface a green / gray "Live" pill and to trigger a catch-up refetch
  // on reconnect (events may have been missed during the gap).
  onConnectionStateChange?: (state: ConnectionState) => void;
  onReconnect?: () => void;
}

export interface QuotationEventStreamHandle {
  close: () => void;
}

export function openQuotationEventStream(
  handlers: QuotationEventStreamHandlers,
): QuotationEventStreamHandle {
  let source: EventSource | null = null;
  let backoff = MIN_BACKOFF_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  let hasConnectedOnce = false;

  function emitState(next: ConnectionState) {
    handlers.onConnectionStateChange?.(next);
  }

  function buildUrl(): string {
    const accessToken = tokenService.getAccessToken();
    const params = new URLSearchParams();
    if (accessToken) params.set('access_token', accessToken);
    return `${API_BASE_URL}${STREAM_PATH}?${params.toString()}`;
  }

  function connect() {
    if (closed) return;
    emitState(hasConnectedOnce ? 'reconnecting' : 'connecting');

    try {
      source = new EventSource(buildUrl());
    } catch {
      // Synchronous EventSource ctor failure (e.g. malformed URL) — schedule retry.
      scheduleReconnect();
      return;
    }

    source.onopen = () => {
      backoff = MIN_BACKOFF_MS; // success resets backoff
      emitState('open');
      if (hasConnectedOnce) {
        // This is a reconnect after a drop, not the very first connect — let
        // the consumer catch up on anything missed during the gap.
        handlers.onReconnect?.();
      }
      hasConnectedOnce = true;
    };

    source.onmessage = (msg) => {
      if (!msg.data) return;
      try {
        const evt = JSON.parse(msg.data) as QuotationEvent;
        handlers.onEvent(evt);
      } catch (err) {
        // Garbled payload — log and skip rather than tearing down the stream.
        console.warn('Failed to parse quotation event payload:', err, msg.data);
      }
    };

    source.onerror = () => {
      // EventSource auto-reconnects, but the spec leaves backoff timing up to
      // the implementation and gives no programmatic state-change events. We
      // close manually + reconnect with our own backoff so the UI gets a stable
      // signal AND we can retry through window-offline / token-expiry paths.
      source?.close();
      source = null;
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (closed) return;
    emitState('reconnecting');
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      // E7 item 4 — the stream's JWT rides in the URL and was captured when it was built, so a reconnect
      // after the 15-minute expiry re-presented a dead token and was rejected again, for ever. Refresh
      // first. Never throws (see the helper) and `closed` is re-checked because the await yields.
      await ensureFreshAccessTokenForStream();
      if (closed) return;
      connect();
    }, backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  }

  connect();

  return {
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
    },
  };
}
